import { type TFunction } from 'i18next'
import { formatPrice } from 'utils/utils'
import {
  isSupersededTier,
  type DiscountStep,
  type GoalFacts,
} from './discountSteps'

// Renders discount-step copy, shared by the cart and the photo styler. Use full
// literal translation keys so the locale-keys usage test detects them.

function getBuyoverTitle(
  goal: Extract<GoalFacts, { goal: 'buyover' }>,
  currency: string,
  t: TFunction,
  compact = false
): string {
  return t(
    compact
      ? 'order.checkout.payment_summary_rows.discount_off_over_price_short'
      : 'order.checkout.payment_summary_rows.discount_off_over_price',
    {
      v1: goal.discountPercent,
      v2: formatPrice(goal.target, currency),
    }
  )
}

function getBundleTitle(
  goal: Extract<GoalFacts, { goal: 'bundle' }>,
  currency: string,
  t: TFunction,
  shownBundleCount: number
): string {
  return t('order.checkout.payment_summary_rows.tiles_size_many', {
    v1: goal.singleBundleTilesCount * shownBundleCount,
    v2: formatPrice(goal.singleBundlePrice * shownBundleCount, currency),
  })
}

// Copy for the active goal still being worked toward. `compact` picks the
// `_short` buyover/bundle keys for tight surfaces (the discount progress bar);
// the order-summary rows keep the full sentences.
export function getStepNotMetText(
  goal: GoalFacts,
  currency: string,
  t: TFunction,
  { compact = false } = {}
): string {
  switch (goal.goal) {
    case 'shipping':
      return t('order.checkout.payment_summary_rows.free_shipping_over', {
        v1: formatPrice(goal.target, currency),
      })
    case 'buyover':
      return `${getBuyoverTitle(goal, currency, t, compact)}, ${t(
        compact
          ? 'order.checkout.payment_summary_rows.buy_over_price_buy_more_short'
          : 'order.checkout.payment_summary_rows.buy_over_price_buy_more',
        { v1: formatPrice(goal.remaining, currency) }
      )}`
    case 'bundle': {
      // A repeat bundle leads with what's already charged, then names this one.
      if (goal.appliedBundlesCount > 1) {
        return `${t('order.checkout.payment_summary_rows.discounts_applied', {
          v1: getBundleTitle(goal, currency, t, goal.appliedBundlesCount - 1),
        })}, ${t(
          'order.checkout.payment_summary_rows.bundle_action.get_next_bundle',
          {
            v1: goal.singleBundleTilesCount * goal.appliedBundlesCount,
            v2: formatPrice(
              goal.singleBundlePrice * goal.appliedBundlesCount,
              currency
            ),
          }
        )}`
      }
      const addText =
        goal.remaining === 1
          ? t(
              compact
                ? 'order.checkout.payment_summary_rows.bundle_action.add_one_short'
                : 'order.checkout.payment_summary_rows.bundle_action.add_one'
            )
          : t(
              compact
                ? 'order.checkout.payment_summary_rows.bundle_action.add_many_short'
                : 'order.checkout.payment_summary_rows.bundle_action.add_many',
              { v1: goal.remaining }
            )
      return `${getBundleTitle(
        goal,
        currency,
        t,
        goal.appliedBundlesCount
      )}, ${addText}`
    }
    case 'percent':
      return ''
  }
}

// Copy when every step is reached: short applied texts joined with ' + ',
// suffixed with "applied".
export function getAllReachedText(
  steps: DiscountStep[],
  currency: string,
  t: TFunction
): string {
  const joined = steps
    .filter((step) => !isSupersededTier(step))
    .map((step) => getStepShortAppliedText(step, currency, t))
    .join(' + ')
  return t('order.checkout.payment_summary_rows.discounts_applied', {
    v1: joined,
  })
}

// Copy once the goal is reached.
export function getStepAppliedText(
  goal: GoalFacts,
  currency: string,
  t: TFunction,
  { totalSavedCopyEnabled = false } = {}
): string {
  switch (goal.goal) {
    case 'shipping':
      return t('order.checkout.payment_summary_rows.eligible_free_shipping')
    case 'buyover':
      return `${getBuyoverTitle(goal, currency, t)}, ${
        totalSavedCopyEnabled
          ? t(
              'order.checkout.payment_summary_rows.buy_over_price_applied_saved',
              { v1: formatPrice(goal.totalSaved, currency) }
            )
          : t('order.checkout.payment_summary_rows.buy_over_price_applied', {
              v1: goal.discountPercent,
            })
      }`
    case 'bundle':
      return getBundleTitle(goal, currency, t, goal.appliedBundlesCount)
    case 'percent':
      return t(
        'order.checkout.payment_summary_rows.discount_percentage_literal',
        {
          v1: goal.percentOff,
        }
      )
  }
}

// Short applied copy per goal, for joining with ' + ' when several are reached.
export function getStepShortAppliedText(
  goal: GoalFacts,
  currency: string,
  t: TFunction
): string {
  switch (goal.goal) {
    case 'shipping':
      return t('order.checkout.payment_summary_rows.free_shipping')
    case 'buyover':
      return t('order.checkout.payment_summary_rows.discount_percentage', {
        v1: goal.discountPercent,
      })
    case 'percent':
      return t('order.checkout.payment_summary_rows.discount_percentage', {
        v1: goal.percentOff,
      })
    case 'bundle':
      return getBundleTitle(goal, currency, t, goal.appliedBundlesCount)
  }
}

// Compact label for the marker chips; shipping renders as an icon.
export function getStepBadgeLabel(goal: GoalFacts, t: TFunction): string {
  switch (goal.goal) {
    case 'shipping':
      return ''
    case 'buyover':
      return `${goal.discountPercent}%`
    case 'percent':
      return `${goal.percentOff}%`
    case 'bundle':
      return t('order.checkout.payment_summary_rows.tiles_count', {
        v1: goal.target,
      })
  }
}
